import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { Reveal } from "@/hooks/useReveal";
import { ArrowUpRight } from "lucide-react";

/**
 * Homepage teaser — sits above the footer.
 * Shows the 3 newest published posts as image-led tiles.
 */
const BlogTeaser = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/posts", { params: { limit: 3 } });
        setPosts(data || []);
      } catch {
        setPosts([]);
      }
    })();
  }, []);

  if (!posts.length) return null;

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  return (
    <section
      id="blog"
      data-testid="blog-teaser-section"
      className="relative bg-black text-white py-28 md:py-40 px-6 md:px-12"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20">
          <div>
            <Reveal>
              <p className="text-[10px] tracking-[0.32em] uppercase mb-6 text-[#B8945A]">
                Insights &amp; Field Notes
              </p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-white">
                From the
                <br />
                <em className="italic font-light">Coach&apos;s Desk.</em>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={200}>
            <Link
              to="/blog"
              data-testid="blog-teaser-view-all"
              className="group inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-[#B8945A] hover:text-white transition-colors"
            >
              View all posts
              <ArrowUpRight
                size={16}
                strokeWidth={1.25}
                className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {posts.map((p, i) => (
            <Reveal key={p.id} delay={i * 120}>
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-teaser-tile-${p.slug}`}
                className="group block relative aspect-[4/5] overflow-hidden bg-[#0a0a0a]"
              >
                {p.hero_image_url ? (
                  <img
                    src={resolveImage(p.hero_image_url)}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] group-hover:scale-105"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-black" />
                )}
                {/* Dark scrim for legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/0" />

                <div className="absolute inset-x-0 bottom-0 p-7 md:p-8 z-10">
                  <p className="text-[10px] tracking-[0.32em] uppercase text-[#B8945A] mb-4">
                    {p.category || "Article"}
                  </p>
                  <h3 className="font-heading text-2xl md:text-[1.7rem] leading-[1.12] tracking-tight text-white">
                    {p.title}
                  </h3>
                  <p className="text-sm text-white/70 mt-4 leading-relaxed line-clamp-2">
                    {p.excerpt}
                  </p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.24em] text-white/40">
                      {fmtDate(p.publish_date)}
                    </span>
                    <span className="text-[10px] tracking-[0.28em] uppercase text-[#B8945A] group-hover:text-white transition-colors">
                      Read &rarr;
                    </span>
                  </div>
                </div>

                {/* Gold underline on hover */}
                <span className="absolute left-7 md:left-8 right-7 md:right-8 bottom-6 h-px bg-[#B8945A] w-0 group-hover:w-[calc(100%-3.5rem)] md:group-hover:w-[calc(100%-4rem)] transition-all duration-700" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogTeaser;
